import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { domainNameToFileName, environment, setDomainConfig } from 'src/environments/environment.prod';
import { BehaviorSubject, Observable, from, switchMap, tap } from 'rxjs';
import { Meta, Title } from '@angular/platform-browser';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { GtmService } from './gtm.service';
import { BrowserService } from 'src/_lib/service/browser.service';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  endpoint: any;
  domain_name: any;
  cdnImagePath: any;
  contact: any;
  formatted_contact: string;
  pageCodeSubject = new BehaviorSubject<string>('');
  pageCode$ = this.pageCodeSubject.asObservable();
  globalSeoSetting: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  domainConfig: any;
  canonicalURL:any;
  robotsTagContent : string;

  constructor(
    private http: HttpClient,
    private title: Title,
    private meta: Meta,
    private browser: BrowserService,
    private gtmService: GtmService,
    @Inject(PLATFORM_ID)
    private platformId: Object,
    @Optional()
    @Inject(REQUEST)
    private request: any,
    @Inject(DOCUMENT) private document: Document
  ) { }

  resolvePageCode(routePageCode?: string): void {
    this.pageCodeSubject.next(routePageCode ?? '');
  }

  private seoData() {
    return tap((res: any) => {
      const response = res?.[0]?.data;
      const pageData = response?.pageData;
      const globalSeo = response?.global_seo_setting;
      this.globalSeoSetting.next(globalSeo);
      if (pageData?.override_meta_description) {
        pageData.override_meta_description = pageData.override_meta_description
          .replace(/\\n/g, ' ')
          .replace(/\n/g, ' ')
          .replace(/\t/g, ' ')
          .replace(/<[^>]*>/g, '')
          .replace(/&quot;/g, '"')
          .replace(/&amp;/g, '&')
          .replace(/\s+/g, ' ')
          .trim();
      }
      this.gtmService.loadGtm(globalSeo.google_tag_manager_header);
      this.applySeo({
        title: pageData?.override_page_title || globalSeo?.page_title,
        description: pageData?.override_meta_description || globalSeo?.meta_description,
        //keywords: pageData?.override_meta_keywords || globalSeo?.meta_keywords,
        type: pageData?.pretty_page_name,
        image_url: pageData?.image_url || globalSeo?.image_url || '',         
        canonicalURL: pageData?.canonical_url || this.canonicalURL || '',
      });
      this.gtmService.pushEvent('page_view', {
        page_title: this.document.title,
        page_location: this.browser.window?.location.href,
        page_path: this.browser.window?.location.pathname,
      });
    });
  }

  //If any changes in this function then please verify with SSR also.
  loadSeo(pageCode: string, key?: string): Observable<any> {
    if (isPlatformServer(this.platformId)) {
      this.domain_name = (this.request?.headers?.host || 'karlkustoms').toLowerCase();
      this.robotsTagContent = this.domain_name.includes('-website.nebhub') ? "noindex, nofollow" : "index, follow"; 
      return from(this.getDomainViseConfiguration(this.domain_name)).pipe(
        switchMap((config: any) => {
          const path = this.request.originalUrl;
          this.canonicalURL = `https://${this.domain_name}${path}`;
          this.endpoint = config.apiUrl;
          this.domain_name = config.domainName;
          this.cdnImagePath = config.cdnImagePath;
          this.contact = config.contact_no;
          this.formatted_contact = this.contact.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
          return this.http.post<any>(`${this.endpoint}seo`, { pageCode, key }).pipe(this.seoData());
        }),
      );
    }
    this.endpoint = environment.apiUrl;
    this.domain_name = environment.domainName;
    this.cdnImagePath = environment.cdnImagePath;
    this.contact = environment.contact_no;
    this.formatted_contact = this.contact.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    return this.http.post<any>(`${this.endpoint}seo`, { pageCode, key }).pipe(this.seoData());
  }

  applySeo(data: {
    title?: string;
    description?: string;
    //keywords?: string;
    type?: string;
    image_url?: string;
    canonicalURL?: string;
  }): void {
    this.title.setTitle(data.title?.trim() || '');
    this.meta.updateTag({ name: 'description', content: data.description?.trim() || '' });
    //this.meta.updateTag({ name: 'keywords', content: data.keywords?.trim() || '' });
    this.meta.updateTag({ property: 'og:title', content: data.title?.trim() || '' });
    this.meta.updateTag({ property: 'og:description', content: data.description?.trim() || '' });
    this.meta.updateTag({ property: 'og:type', content: data.type?.trim() || '' });
    this.meta.updateTag({ property: 'og:image', content: this.cdnImagePath + data.image_url?.trim() || '' });
    this.meta.updateTag({ property: 'og:site_name', content: this.domain_name.trim() || '' });
    this.meta.updateTag({ property: 'og:phone_number', content: this.formatted_contact.trim() || '' });
    this.meta.updateTag({ property: 'og:url', content: this.canonicalURL || '' });
    this.meta.updateTag({ name: 'twitter:title', content: data.title?.trim() || '' });
    this.meta.updateTag({ name: 'twitter:description', content: data.description?.trim() || '' });
    this.meta.updateTag({ name: 'robots', content: this.robotsTagContent || '' });
    const canonical = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (data.canonicalURL) { canonical.href = data.canonicalURL; }
  }

  getGlobalSeoSetting() {
    return this.globalSeoSetting.value;
  }

  async getDomainViseConfiguration(domainName: any) {
    const isDevHost = domainName === 'localhost' || domainName === 'angular_dev';
    const fileName = isDevHost ? 'env' : domainNameToFileName(domainName);
    let domainConfig: any;
    try {
      domainConfig = await import(`src/environments/${fileName}.json`);
    } catch {
      domainConfig = await import('src/environments/env.json');
    }
    return domainConfig.default;
  }

  applyProductSchema(data: {
    name: string;
    sku?: string;
    mpn?: string;
    gtin?: string;
    brand?: string;
    price?: number;
    minPrice?: number;
    maxPrice?: number;
    priceCurrency?: string;
    availability?: boolean;
    images?: string[];
    description?: string;
    avgRating?: number;
    reviewCount?: number;
    reviews?: { author: string; rating: number; body?: string; datePublished?: string }[];
    canonicalUrl?: string;
    shippingMessage?: string;
    breadcrumb?: { name: string; url?: string }[];
  }): void {
    if (typeof document !== 'undefined') {
    const origin = this.document.location.origin;
    const canonical = data.canonicalUrl || this.document.location.href;
    let canonicalEl = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalEl) {
      canonicalEl = this.document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      this.document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonical);
    const ogTags: { [prop: string]: string } = {
      'og:type': 'product',
      'og:title': data.name,
      'og:url': canonical,
      'og:description': data.description || '',
      'og:image': data.images?.[0] || '',
      'product:price:amount': data.price != null ? String(data.price) : '',
      'product:price:currency': data.priceCurrency || 'USD',
    };
    for (const [property, content] of Object.entries(ogTags)) {
      if (!content) continue;
      let el = this.document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!el) {
        el = this.document.createElement('meta');
        el.setAttribute('property', property);
        this.document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    }
    const availabilityUrl = data.availability
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock';
    const currency = data.priceCurrency || 'USD';
    const offer: any = {
      '@type': 'Offer',
      priceCurrency: currency,
      availability: availabilityUrl,
      url: canonical,
    };
    if (data.price != null && data.price > 0) {
      offer.price = data.price.toFixed(2);
    } else if (data.minPrice != null && data.minPrice > 0) {
      offer.price = data.minPrice.toFixed(2);
      offer.priceSpecification = {
        '@type': 'PriceSpecification',
        minPrice: data.minPrice.toFixed(2),
        maxPrice: (data.maxPrice ?? data.minPrice).toFixed(2),
        priceCurrency: currency,
      };
    }
    offer.shippingDetails = {
      '@type': 'OfferShippingDetails',
      shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'US' },
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        handlingTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 1, unitCode: 'DAY' },
        transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 5, unitCode: 'DAY' },
      },
      shippingRate: { '@type': 'MonetaryAmount', value: '0', currency },
    };
    offer.hasMerchantReturnPolicy = {
      '@type': 'MerchantReturnPolicy',
      applicableCountry: 'US',
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 30,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/FreeReturn',
    };
    const images = data.images?.length ? data.images : undefined;
    const schema: any = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: data.name,
      description: data.description || undefined,
      image: images,
      sku: data.sku || undefined,
      ...(data.mpn ? { mpn: data.mpn } : {}),
      ...(data.gtin ? { gtin: data.gtin } : {}),
      brand: data.brand ? { '@type': 'Brand', name: data.brand } : undefined,
      offers: offer,
    };
    if (data.avgRating && data.reviewCount && data.reviewCount > 0) {
      schema.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: data.avgRating.toFixed(1),
        bestRating: '5',
        worstRating: '1',
        reviewCount: data.reviewCount,
      };
    }
    if (data.reviews?.length) {
      schema.review = data.reviews.map(r => ({
        '@type': 'Review',
        author: { '@type': 'Person', name: r.author },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: r.rating.toFixed(1),
          bestRating: '5',
          worstRating: '1',
        },
        ...(r.body ? { reviewBody: r.body } : {}),
        ...(r.datePublished ? { datePublished: r.datePublished } : {}),
      }));
    }
    this.setJsonLd('product-schema', schema);
    if (data.breadcrumb?.length) {
      const breadcrumbItems = data.breadcrumb.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.name,
        item: item.url ? `${origin}${item.url}` : undefined,
      }));
      const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbItems,
      };
      this.setJsonLd('breadcrumb-schema', breadcrumbSchema);
    }
  }
  }

  private setJsonLd(id: string, schema: object): void {
    if (typeof document !== 'undefined') {
    let el = this.document.getElementById(id) as HTMLScriptElement;
    if (!el) {
      el = this.document.createElement('script');
      el.id = id;
      el.setAttribute('type', 'application/ld+json');
      this.document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schema);
  }
}
}
